import { useEffect, useMemo } from 'react';

import { CinemaDto } from '@cineamo/api/models/cinema/CinemaDto.types';
import { MovieDto } from '@cineamo/api/models/movie/v2/MovieDto.types';
import { ShowingDto } from '@cineamo/api/models/showing/ShowingDto.types';

import type { UrlQueryParameter } from '@cineamo/api/helpers/url-query-helper';
import { getShowings } from '@cineamo/api/resources/showing/showing-api';
import { ShowingApiEmbed } from '@cineamo/api/resources/showing/showing-api.embed';

import {
    formatDate_yyyyMMddTHHmmssZ,
    logicalStartOfToday,
    getLogicalStartOfDay,
    getLogicalEndOfDay
} from '@cineamo/ui/src/helpers/date-helper';
import { notEmptyValue } from '@cineamo/ui/src/helpers/type-helper';

import { getPreviewsEndDatetime, PREVIEWS_SELECTED_DATE } from '../showtimes-finder/previewsUtils';
import useLocationStore from '../stores/location/useLocationStore';

import { usePagination } from './usePagination';

export type CinemaWithShowings = CinemaDto & { showings: ShowingDto[] };

const MAX_SHOWINGS = 100;

export function useCinemasShowingsSearch(
    movieId: MovieDto['id'],
    selectedDate: string,
    apiFilters?: UrlQueryParameter,
    // `releaseDate` is only consulted when selectedDate === PREVIEWS_SELECTED_DATE
    options?: { enabled?: boolean; releaseDate?: string }
) {
    // Deliberately no `distance`: this hook always searches for a specific movie, and a radius
    // must not hide cinemas that play it. The location-store radius is due to be removed entirely.
    const { longitude, latitude, isAllLocationDetailsAvailable } = useLocationStore();
    const hasLocation = isAllLocationDetailsAvailable();
    const isEnabled = options?.enabled !== false;
    const isPreviews = selectedDate === PREVIEWS_SELECTED_DATE;
    const previewsReleaseDate = options?.releaseDate;

    // Extract time-of-day params (not actual API params) and separate server filters
    const { startTime, endTime, ...serverFilters } = apiFilters ?? {};

    const {
        data: showingsResponse,
        hasNextPage,
        isLoading,
        isFetchingNextPage,
        fetchNextPage,
        ref: loaderRef
    } = usePagination(
        [
            'showtimes',
            `movieId-${movieId}`,
            `longitude-${longitude}`,
            `latitude-${latitude}`,
            `selectedDate-${selectedDate}`,
            `releaseDate-${isPreviews ? previewsReleaseDate : ''}`,
            JSON.stringify(apiFilters ?? {})
        ],
        async (page) => {
            if (latitude === undefined || longitude === undefined) {
                return null;
            }

            // PREVIEWS spans multiple days (now → day before the release date), so the
            // time-of-day filters don't apply — it uses a fixed range regardless of
            // startTime/endTime.
            // Compute startDatetime considering startTime hour
            let startDate: Date;
            if (selectedDate === 'all' || isPreviews) {
                startDate = logicalStartOfToday();
            } else if (typeof startTime === 'number') {
                startDate = new Date(selectedDate);
                startDate.setHours(startTime, 0, 0, 0);
            } else {
                startDate = getLogicalStartOfDay(selectedDate);
            }

            // Compute endDatetime considering endTime hour
            let endDate: Date | undefined;
            if (isPreviews) {
                // Stop before the release day starts; previewsReleaseDate is guaranteed present
                // because the PREVIEWS view is only enabled once the release date is known.
                endDate = previewsReleaseDate ? getPreviewsEndDatetime(previewsReleaseDate) : undefined;
            } else if (selectedDate === 'all') {
                endDate = undefined;
            } else if (typeof endTime === 'number') {
                endDate = new Date(selectedDate);
                endDate.setHours(endTime, 0, 0, 0);
            } else {
                endDate = getLogicalEndOfDay(selectedDate);
            }

            return getShowings({
                movieId,
                startDatetime: formatDate_yyyyMMddTHHmmssZ(startDate),
                ...(endDate ? { endDatetime: formatDate_yyyyMMddTHHmmssZ(endDate) } : {}),
                embed: [ShowingApiEmbed.Cinema],
                longitude: Number(longitude),
                latitude: Number(latitude),
                sort: 'distance',
                order: 'asc',
                page,
                ...serverFilters
            });
        },
        {
            enabled: hasLocation && isEnabled
        }
    );

    const allShowings = useMemo(() => {
        const uniqueShowingIds = new Set<string>();
        const showings = showingsResponse?.pages?.flatMap((page) => page?._embedded?.showings ?? []) ?? [];
        return showings.filter((showing) => {
            const showingId = showing._embedded?.cinema?.id + ' - ' + showing.id;
            if (uniqueShowingIds.has(showingId)) {
                return false;
            }
            uniqueShowingIds.add(showingId);
            return true;
        });
    }, [showingsResponse]);

    const cinemas = useMemo(
        () =>
            Array.from(
                new Map(
                    allShowings
                        .map((showing) => showing._embedded?.cinema)
                        .filter(notEmptyValue)
                        .map((cinema) => [cinema.id, cinema])
                ).values()
            ),
        [allShowings]
    );
    const cinemasWithShowings: Array<CinemaWithShowings> = useMemo(
        () =>
            cinemas.map((cinema) => {
                const showings = allShowings
                    .filter((showing) => showing._embedded?.cinema?.id === cinema.id)
                    .sort((a, b) => (a.startDatetime ?? '').localeCompare(b.startDatetime ?? ''));
                return {
                    ...cinema,
                    showings: showings
                };
            }),
        [allShowings, cinemas]
    );

    useEffect(() => {
        if (hasNextPage && allShowings.length < MAX_SHOWINGS) {
            // fetch pages in the background until we hit the list end or MAX_SHOWINGS
            fetchNextPage();
        }
    }, [hasNextPage, fetchNextPage, allShowings.length]);

    const result = useMemo(
        () => ({
            totalItems: showingsResponse?.pages?.[0]?._total_items,
            cinemasWithShowings,
            hasNextPage,
            isLoading,
            isFetchingNextPage,
            loaderRef
        }),
        [cinemasWithShowings, hasNextPage, isLoading, isFetchingNextPage, loaderRef, showingsResponse?.pages]
    );

    return result;
}
